import type { AppView, AuthPane } from '../navigation/AppNavigationContext';
import {
  browseParamsFromAssistantTurn,
  type AssistantTurnResponse,
} from '../api/assistantTurn';
import {
  formatAssistantCitySuggestionList,
  type AssistantIntent,
  type AssistantSearchIntent,
} from './parseAssistantIntent';
import { resolveAssistantSearchIntent } from './mergeAssistantSearch';
import { ASSISTANT_FAQ_MESSAGE_KEYS } from './faqAnswers';
import { assistantSearchChipFromBrowse, assistantSearchChipFromIntent } from './formatAssistantSearchChip';
import type { AssistantPriceConstraint } from './assistantPriceConstraint';
import { preferInclusiveAssistantPrice } from './assistantPriceConstraint';
import type { AssistantPriceSort } from './parseAssistantPriceSort';
import type { AssetBrowseListSortMode } from '../utils/assetBrowseListSort';
import {
  mergeAssistantScheduleOverlay,
  nextAssistantScheduleDateKey,
  type AssistantSchedule,
} from './assistantSchedule';
import { firstDayOfAssistantMonth } from './parseAssistantWhen';
import { localizeAssetTypeLabel } from '../i18n/assetTypeLabels';
import type { MessageKey } from '../i18n/messages';
import type { AppLanguage } from '../i18n/types';
import type { FavoriteAssetEntry } from '../favorites/favoriteAssetsStorage';
import { commitAssistantSearchHits } from './assistantSearchHits';

export type AssistantExecutionResult =
  | {
      ok: true;
      summaryKey: MessageKey;
      summaryParams?: Record<string, string | number>;
      /** Reply names the cheapest / most expensive listing once results settle. */
      priceSort?: AssistantPriceSort;
      /** Sort current hits by distance to this street (geocode + reply in the controller). */
      proximityStreet?: string;
      proximityStreetDisplay?: string;
      proximityCity?: string;
    }
  | { ok: true; summaryText: string }
  | { ok: false; errorKey: MessageKey; errorParams?: Record<string, string | number> }
  | {
      ok: true;
      pendingBookConfirm: true;
      assetQuery: string | null;
      summaryKey?: MessageKey;
      summaryText?: string;
    };

export type AssistantOpenAssetsParams = {
  assetTypeId?: number | null;
  assetTypeLabel?: string | null;
  catalogBrowse?: boolean;
  returnView?: 'home' | 'search' | 'favorites' | null;
  listSearch?: string | null;
  countryId?: number | null;
  city?: string | null;
  whenDate?: string | null;
  whenMonth?: string | null;
  whenTime?: string | null;
  street?: string | null;
  officeFamily?: boolean;
  open24h?: boolean;
  /** Spoken amenities — hard facility-icon refine (kitchen / wifi / parking). */
  amenityIcons?: string[] | null;
  /** Hourly/daily price: less / more / equals. */
  price?: AssistantPriceConstraint | null;
  /** Recurring weekly request from turn / local overlay. */
  schedule?: AssistantSchedule | null;
  /** Backend turn `asset_ids` — prefer these listings when the catalog dump contains them. */
  assetIds?: string[] | null;
  /** Price or distance list order (assistant “cheapest” / “closest to”). */
  listSort?: AssetBrowseListSortMode | null;
  /** Geocoded street / place used as the distance-sort origin (assistant “closest to”). */
  proximityAnchor?: { lat: number; lng: number } | null;
};

export type AssistantNavApi = {
  view: AppView;
  openSearch: (initialKeyword?: string) => void;
  updateSearchQuery: (query: string | null) => void;
  openFavorites: () => void;
  openMessages: (peerUserId?: string | null) => void;
  openProfile: () => void;
  openAssets: (params?: AssistantOpenAssetsParams) => void;
  openBookings: (options?: { preserveCreateFlow?: boolean }) => void;
  openAuth: (pane?: AuthPane) => void;
  navigateFromBottomBar: (target: AppView) => boolean;
  setView: (view: AppView) => void;
};

type TranslateFn = (key: MessageKey, params?: Record<string, string | number>) => string;

export type ExecuteAssistantIntentOptions = {
  nav: AssistantNavApi;
  t: TranslateFn;
  language?: AppLanguage;
  /** Last catalog search, so a follow-up can narrow those results. */
  previousSearch?: AssistantSearchIntent | null;
  /** Local parser price when the turn omitted it. */
  priceOverlay?: AssistantPriceConstraint | null;
  /** Local schedule overlay when the turn omitted `browse.schedule`. */
  scheduleOverlay?: AssistantSchedule | null;
  /** When true, book intent returns a confirm payload instead of opening booking. */
  deferBookConfirm?: boolean;
  /** Clears every favourite; returns how many were removed. */
  clearAllFavorites?: () => number;
  /** Current favourite snapshots — used when searching “in my favorites”. */
  favoriteAssets?: readonly FavoriteAssetEntry[];
};

function citySearchDisplay(city: string | null): string {
  if (!city?.trim()) {
    return '';
  }
  return city.split(/\s*[-–]\s*/)[0]?.trim() || city.trim();
}

function searchSummaryType(
  intent: Extract<AssistantIntent, { kind: 'search' }>,
  language: AppLanguage,
  t: TranslateFn,
): string {
  if (intent.officeFamily) {
    return t('assetType.office');
  }
  const label = intent.assetTypeLabel?.trim();
  if (!label) {
    return '';
  }
  return localizeAssetTypeLabel(label, language, t);
}

function listSearchForSearchIntent(intent: Extract<AssistantIntent, { kind: 'search' }>): string | null {
  const venue = intent.venue?.trim() || '';
  const hasType = Boolean(intent.assetTypeLabel || intent.officeFamily);
  // Distance sort to a street — never AND a venue keyword filter.
  if (intent.proximityStreet?.trim()) {
    return null;
  }
  // Type + street + city is already a full browse. Do not AND leftover “כמה / how many”.
  if (hasType && intent.city?.trim() && intent.street?.trim()) {
    return null;
  }
  // Amenities use amenityIcons hard-filter — never title keyword search.
  // Follow-ups like “וגם עם מרחב מוגן” must not AND a leftover “וגם” venue keyword.
  if (venue && !(intent.amenityIcons && intent.amenityIcons.length > 0)) {
    return venue;
  }
  if (
    intent.city ||
    intent.street ||
    intent.proximityStreet ||
    intent.assetTypeLabel ||
    intent.priceSort ||
    intent.amenityIcons?.length
  ) {
    return null;
  }
  return intent.keyword.trim() || null;
}

/** Place leftover without a city/street — keyword-search the name; type is a soft AND on first search. */
function isPlaceNameWithoutCityOrStreet(
  intent: Extract<AssistantIntent, { kind: 'search' }>,
): boolean {
  return Boolean(intent.venue?.trim()) && !intent.city && !intent.street;
}

function browseFieldsFromSearchIntent(
  intent: Extract<AssistantIntent, { kind: 'search' }>,
  returnView: 'home' | 'search' | 'favorites' | null,
): AssistantOpenAssetsParams {
  const schedule = intent.schedule ?? null;
  const whenMonth = intent.whenMonth?.trim() || null;
  // Month window is soft (any day); seed the calendar on the 1st without treating it as “only that day”.
  const whenDate =
    intent.whenDate ||
    (whenMonth ? firstDayOfAssistantMonth(whenMonth) : null) ||
    (schedule ? nextAssistantScheduleDateKey(schedule) : null);
  return {
    assetTypeLabel: intent.assetTypeLabel,
    catalogBrowse: true,
    listSearch: listSearchForSearchIntent(intent),
    countryId: intent.countryId,
    city: intent.city,
    whenDate,
    whenMonth,
    whenTime: intent.whenTime || schedule?.start_time || null,
    street: intent.streetDisplay || intent.street,
    officeFamily: Boolean(intent.officeFamily),
    open24h: Boolean(intent.open24h),
    amenityIcons: intent.amenityIcons?.length ? intent.amenityIcons : null,
    price: intent.price ?? null,
    schedule,
    assetIds: intent.assetIds?.length ? intent.assetIds : null,
    listSort: intent.priceSort ?? (intent.proximityStreet ? 'distance' : null),
    returnView,
  };
}

export function catalogBrowseParamsForSearchIntent(
  intent: AssistantIntent,
  returnView: 'home' | 'search' | 'favorites' | null = 'home',
): AssistantOpenAssetsParams | null {
  if (intent.kind !== 'search') {
    return null;
  }
  if (isPlaceNameWithoutCityOrStreet(intent)) {
    if (!intent.assetTypeLabel && !intent.officeFamily) {
      return null;
    }
    return browseFieldsFromSearchIntent(intent, returnView);
  }
  if (
    !intent.assetTypeLabel &&
    !intent.city &&
    !intent.street &&
    intent.countryId == null &&
    !intent.whenDate &&
    !intent.whenMonth &&
    !intent.whenTime &&
    !intent.open24h &&
    !(intent.amenityIcons && intent.amenityIcons.length > 0) &&
    !intent.price &&
    !intent.priceSort &&
    !intent.proximityStreet &&
    !intent.schedule &&
    !(intent.assetIds && intent.assetIds.length > 0) &&
    !intent.favoritesOnly
  ) {
    return null;
  }
  return browseFieldsFromSearchIntent(intent, returnView);
}

function applyFavoritesScopeToSearchIntent(
  intent: AssistantSearchIntent,
  favoriteAssets: readonly FavoriteAssetEntry[] | undefined,
): AssistantSearchIntent | { empty: true } {
  if (!intent.favoritesOnly) {
    return intent;
  }
  const entries = favoriteAssets ?? [];
  if (entries.length === 0) {
    return { empty: true };
  }
  const assetIds = entries.map((entry) => entry.id).filter(Boolean);
  return {
    ...intent,
    assetIds: assetIds.length > 0 ? assetIds : intent.assetIds ?? null,
    venue: null,
  };
}

export function executeAssistantIntent(
  intent: AssistantIntent,
  options: ExecuteAssistantIntentOptions,
): AssistantExecutionResult {
  const {
    nav,
    t,
    language = 'en',
    previousSearch = null,
    deferBookConfirm = true,
    clearAllFavorites,
    favoriteAssets,
  } = options;
  const resolvedSearch =
    intent.kind === 'search'
      ? resolveAssistantSearchIntent(intent, previousSearch, nav.view)
      : null;

  switch (intent.kind) {
    case 'search': {
      const resolved = resolvedSearch?.intent ?? intent;
      const scoped = applyFavoritesScopeToSearchIntent(resolved, favoriteAssets);
      if ('empty' in scoped) {
        return { ok: false, errorKey: 'assistant.result.favoritesSearchEmpty' };
      }
      const active = scoped;
      const refined = resolvedSearch?.refined ?? false;
      const keyword = active.keyword.trim();
      const browse = catalogBrowseParamsForSearchIntent(active, 'home');
      const chip = assistantSearchChipFromIntent(active, language);
      if (browse) {
        nav.openAssets(browse);
        if (
          active.favoritesOnly &&
          !refined &&
          favoriteAssets &&
          favoriteAssets.length > 0
        ) {
          // Fresh “in my favorites” search: seed hits from saved snapshots (may be
          // missing from the catalog dump). Follow-ups keep the prior hit list.
          commitAssistantSearchHits(
            favoriteAssets.map((entry) => entry.asset),
            { loaded: true },
          );
        }
        if (intent.priceSort && active.priceSort) {
          return {
            ok: true,
            summaryKey: 'assistant.result.refine',
            summaryParams: {
              q: keyword || active.raw,
              type: active.assetTypeLabel ?? '',
              city: active.city ?? '',
              chip: chip ?? keyword ?? active.raw,
            },
            priceSort: active.priceSort,
          };
        }
        if (intent.proximityStreet && active.proximityStreet) {
          return {
            ok: true,
            summaryKey: 'assistant.result.refine',
            summaryParams: {
              q: keyword || active.raw,
              type: active.assetTypeLabel ?? '',
              city: active.city ?? '',
              chip: chip ?? keyword ?? active.raw,
            },
            proximityStreet: active.proximityStreet,
            proximityStreetDisplay: active.proximityStreetDisplay ?? active.proximityStreet,
            proximityCity: active.city ?? '',
          };
        }
        if (refined) {
          return {
            ok: true,
            summaryKey: 'assistant.result.refine',
            summaryParams: {
              q: keyword || active.raw,
              type: active.assetTypeLabel ?? '',
              city: active.city ?? '',
              chip: chip ?? keyword ?? active.raw,
            },
          };
        }
        if (chip) {
          return {
            ok: true,
            summaryKey: active.assetTypeLabel
              ? 'assistant.result.searchTypeSlot'
              : 'assistant.result.searchSlot',
            summaryParams: {
              q: keyword || active.raw,
              type: searchSummaryType(active, language, t),
              city: citySearchDisplay(active.city),
              chip,
            },
          };
        }
        const typeLabel = searchSummaryType(active, language, t);
        const cityLabel = citySearchDisplay(active.city);
        return {
          ok: true,
          summaryKey: typeLabel && cityLabel
            ? 'assistant.result.searchTypeCity'
            : typeLabel
              ? 'assistant.result.browseType'
              : cityLabel
                ? 'assistant.result.searchCity'
                : 'assistant.result.search',
          summaryParams: {
            q: keyword || active.raw,
            type: typeLabel,
            city: cityLabel,
          },
        };
      }
      nav.openSearch(keyword || active.raw);
      return {
        ok: true,
        summaryKey: 'assistant.result.search',
        summaryParams: {
          q: keyword || active.raw,
          type: active.assetTypeLabel ?? '',
          city: active.city ?? '',
        },
      };
    }
    case 'navigate': {
      switch (intent.target) {
        case 'home':
          nav.navigateFromBottomBar('home');
          break;
        case 'search':
          nav.openSearch();
          break;
        case 'favorites':
          nav.openFavorites();
          break;
        case 'messages':
          nav.openMessages();
          break;
        case 'profile':
          nav.openProfile();
          break;
        case 'assets':
          nav.openAssets({ catalogBrowse: true });
          break;
        case 'bookings':
          nav.openBookings();
          break;
        case 'auth':
          nav.openAuth('login');
          break;
        default:
          break;
      }
      return {
        ok: true,
        summaryKey: 'assistant.result.navigate',
        summaryParams: { target: intent.target },
      };
    }
    case 'faq': {
      const key = ASSISTANT_FAQ_MESSAGE_KEYS[intent.topic];
      return { ok: true, summaryText: t(key) };
    }
    case 'book': {
      if (deferBookConfirm) {
        return {
          ok: true,
          pendingBookConfirm: true,
          assetQuery: intent.assetQuery,
          summaryKey: 'assistant.result.bookConfirmPrompt',
        };
      }
      return { ok: false, errorKey: 'assistant.error.bookNeedsConfirm' };
    }
    case 'clearFavorites': {
      const removed = clearAllFavorites?.() ?? 0;
      nav.openFavorites();
      if (removed <= 0) {
        return { ok: true, summaryKey: 'assistant.result.clearFavoritesEmpty' };
      }
      return {
        ok: true,
        summaryKey: 'assistant.result.clearFavorites',
        summaryParams: { count: removed },
      };
    }
    case 'clarifyPlace': {
      const options = formatAssistantCitySuggestionList(intent.suggestions, language);
      const key =
        intent.clarify === 'streetOrCity'
          ? 'assistant.clarify.streetOrCity'
          : 'assistant.clarify.citySuggestions';
      return {
        ok: true,
        summaryKey: key,
        summaryParams: {
          heard: intent.heard,
          options: options || intent.heard,
        },
      };
    }
    case 'unknown':
    default:
      return { ok: false, errorKey: 'assistant.error.unknown' };
  }
}

/** Apply a successful `POST /assistant/turn` payload. Callers fall back to {@link executeAssistantIntent} when this is skipped. */
export function executeAssistantTurn(
  turn: AssistantTurnResponse,
  options: Pick<
    ExecuteAssistantIntentOptions,
    'nav' | 'previousSearch' | 'language' | 'priceOverlay' | 'scheduleOverlay'
  > & {
    /** Local parser “cheapest / most expensive” — the turn payload has no sort field. */
    priceSortOverlay?: AssistantPriceSort | null;
    /** Local “עם מטבח” / wifi / parking — turn browse often omits facilities. */
    amenityIconsOverlay?: string[] | null;
  },
): AssistantExecutionResult {
  const {
    nav,
    previousSearch = null,
    language = 'en',
    priceOverlay = null,
    priceSortOverlay = null,
    scheduleOverlay = null,
    amenityIconsOverlay = null,
  } = options;
  if (turn.action === 'search' || turn.action === 'refine') {
    const browse: AssistantOpenAssetsParams = browseParamsFromAssistantTurn(turn);
    browse.price = preferInclusiveAssistantPrice(browse.price, priceOverlay);
    browse.schedule = mergeAssistantScheduleOverlay(browse.schedule, scheduleOverlay);
    if (amenityIconsOverlay?.length) {
      const merged = Array.from(
        new Set([...(browse.amenityIcons ?? []), ...amenityIconsOverlay].filter(Boolean)),
      );
      browse.amenityIcons = merged.length > 0 ? merged : null;
    }
    if (browse.schedule?.start_time && !browse.whenTime) {
      browse.whenTime = browse.schedule.start_time;
    }
    if (browse.schedule && !browse.whenDate) {
      browse.whenDate = nextAssistantScheduleDateKey(browse.schedule);
    }
    browse.listSort = priceSortOverlay;
    const keptPreviousIds =
      (!browse.assetIds || browse.assetIds.length === 0) && Boolean(previousSearch?.assetIds?.length);
    if (keptPreviousIds && previousSearch?.assetIds?.length) {
      browse.assetIds = previousSearch.assetIds;
    }
    nav.openAssets(browse);
    if (keptPreviousIds) {
      const chip = assistantSearchChipFromBrowse(browse, language);
      return {
        ok: true,
        summaryKey: 'assistant.result.refine',
        summaryParams: {
          q: turn.say,
          type: browse.assetTypeLabel ?? '',
          city: browse.city ?? '',
          chip: chip ?? browse.city ?? '',
        },
      };
    }
    if (priceSortOverlay) {
      return {
        ok: true,
        summaryKey: 'assistant.result.refine',
        summaryParams: {
          q: turn.say,
          type: browse.assetTypeLabel ?? '',
          city: browse.city ?? '',
          chip: assistantSearchChipFromBrowse(browse, language) ?? browse.city ?? '',
        },
        priceSort: priceSortOverlay,
      };
    }
    return { ok: true, summaryText: turn.say };
  }
  if (turn.action === 'propose_book') {
    return {
      ok: true,
      pendingBookConfirm: true,
      assetQuery: turn.assetIds[0] ?? null,
      summaryText: turn.say,
    };
  }
  return { ok: true, summaryText: turn.say };
}
